<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <RequestLifeCycle
            ref="requestLifeCycle"
            :life-cycle="request.lifeCycle"
        />
        <b-card ref="verticalStepComponent" id="simple-flow">
            <b-row class="mb-1">
                <b-col md="4" class="d-flex align-items-center">
                    <span class="align-middle">
                        <StatusBadge
                            type="received"
                            :status="request.status"
                        />
                    </span>
                </b-col>
                <b-col md="8">
                    <Actions
                        :request="request"
                        :show-save="isSaveButtonVisible"
                        :is-reply-disabled="isReplyDisabled"
                        :show-reply="isReplyButtonVisible"
                        :show-forward="false"
                        :is-life-cycle-visible="request.lifeCycle.length > 0"
                        @showLifeCycle="showLifeCycle"
                        @close="close"
                        class="float-right"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <h2 class="text-center">
                        {{ $t('Page(ReceivedRequest):Component(Text):page-label', {customer: request.customer.title, product: getProductTitle()}) }}
                    </h2>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <ViewRequest
                        :request="request"
                    />
                </b-col>
            </b-row>

            <h4 class="reply-process-heading">{{ $t('Page(ReceivedRequest):Component(Text):reply-process') }}</h4>

            <app-collapse
                type="margin"
                class="vertical-steps-flow"
            >
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-general-information-label')"
                    :header-bg-variant="firstStepStyle.bg"
                    :header-text-variant="firstStepStyle.text"
                    :class="firstStepStyle.class"
                    is-visible
                    ref="step1"
                >
                    <LegacyBulkProductInformation
                        v-if="isExternalBulkReplyMode"
                        class="mt-1"
                        :request-id="id"
                        :product-id="productId"
                        :request="request"
                        :product="product"
                        :candidates="bulkReplyCandidates"
                        :selected-request-ids="selectedBulkReplyRequestIds"
                        :loading="bulkReplyCandidatesLoading"
                        :busy="bulkReplySelectionUpdating"
                        :error="bulkReplyCandidatesError"
                        @validityChanged="(isValid) => changeStepValidity(1, isValid)"
                        @remove="removeBulkReplyRequest"
                        @retry="loadBulkReplyCandidates({ force: true })"
                    />
                    <ProductInformation
                        v-else
                        ref="ProductInformation"
                        class="mt-1"
                        :product="product"
                        :request="request"
                        :customer-product="request.customerProduct"
                        :included-substances="includedSubstances"
                        @validityChanged="(isValid) => changeStepValidity(1, isValid)"
                        @change="saveProduct"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(1)"
                            @click="() => doneStep(1)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('ce')"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-concern-elements-label')"
                    :class="getStepVariantStyles(2).class"
                    :header-bg-variant="getStepVariantStyles(2).bg"
                    :header-text-variant="getStepVariantStyles(2).text"
                    ref="step2"
                >
                    <ConcernElements
                        class="simple-flow-step-2"
                        v-if="renderStage > 1"
                        ref="ConcernElementsComponent"
                        :request="request"
                        :product="product"
                        :compact-view="concernElementsCompactView"
                        :included-substances="includedSubstances"
                        :excluded-substances="excludedSubstances"
                        :loading="saveSubstanceLoading"
                        @validityChanged="(isValid) => changeStepValidity(2, isValid)"
                        @undefined="markSubstanceUndefined"
                        @include="markSubstanceIncluded"
                        @exclude="markSubstanceExcluded"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(2)"
                            @click="() => doneStep(2)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('verification')"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-verification-documents-label')"
                    :class="getStepVariantStyles(3).class"
                    :header-bg-variant="getStepVariantStyles(3).bg"
                    :header-text-variant="getStepVariantStyles(3).text"
                    ref="step3"
                >
                    <InstructionText class="simple-flow-instruction">
                        {{ $t('Page(ReceivedRequestSimpleFlow):Component(Instruction):verification-documents') }}
                    </InstructionText>
                    <Overlay :show="showDocumentLoading">
                        <Verification
                            class="simple-flow-step-3"
                            ref="verification"
                            :product="product"
                            :id="productId"
                            :visible-elements="verificationDocuments"
                            :compact-view="verificationCompactView"
                            :evidence="evidenceReports"
                            :compliant-statement="compliantStatement"
                            :compliant-statement-border-variant="compliantStatementStyle.border"
                            :evidence-report-border-variant="evidenceReportStyle.border"
                            :manage-predefined="false"
                            :requested-legislations="request.legislations"
                            :default-compliant-statement-legislation-list="requestedLegislationIds"
                            :default-evidence-report-legislation-list="requestedLegislationIds"
                            @addCompliantStatement="addCompliantStatement"
                            @removeCompliantStatement="removeCompliantStatement"
                            @compliantStatementChange="documentUpdated"
                            @evidenceReportChange="documentUpdated"
                            @addReport="addReport"
                            @removeReport="removeEvidenceReport"
                        />
                    </Overlay>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(3)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('other-information')"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-other-information-label')"
                    :class="getStepVariantStyles(4).class"
                    :header-bg-variant="getStepVariantStyles(4).bg"
                    :header-text-variant="getStepVariantStyles(4).text"
                    ref="step4"
                >
                    <Overlay :show="otherReplyInformationLoading">
                        <InstructionText class="simple-flow-instruction">
                            <span v-html="$t('Page(ReceivedRequestSimpleFlow):Component(Instruction):other-information')"/>
                        </InstructionText>

                        <OtherReplyInformation
                            class="simple-flow-step-4"
                            :request="request"
                            @validityChange="(isValid) => changeStepValidity(4, isValid)"
                            @input="onOtherInformationChange"
                        />

                        <div class="text-center">
                            <DoneGoToNextStep
                                class="mt-1"
                                :disabled="!isStepValid(4)"
                                @click="() => doneStep(4)"
                            />
                        </div>
                    </Overlay>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(SimpleFlowStepItem):step-approve-declaration')"
                    v-if="isStepVisible('approve-declaration') && isApproveDeclarationStepVisible"
                    :disabled="isApproveDeclarationStepDisabled"
                    :class="getStepVariantStyles(5).class"
                    :header-bg-variant="getStepVariantStyles(5).bg"
                    :header-text-variant="getStepVariantStyles(5).text"
                    ref="step5"
                >
                    <InstructionText class="simple-flow-instruction">
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):approve-declaration')"/>
                    </InstructionText>
                    <DeclarationApproval
                        class="no-border simple-flow-step-5"
                        label=""
                        :request-id="id"
                        @confirmed="declarationApproved"
                    />
                </app-collapse-item>
                <app-collapse-item
                    v-if="isFinalStepVisible"
                    :disabled="!isReplyStepActive"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-final')"
                    ref="stepSendReply"
                >
                    <SendReply
                        :reply-details="request.replyDetails"
                        @send="sendReply"
                    />
                </app-collapse-item>
            </app-collapse>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BRow, BCol, BOverlay} from 'bootstrap-vue'
import {Actions} from "@/views/request/received/components";
import StatusBadge from "@/views/request/components/StatusBadge";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {ProductCardMixins} from "@/views/product/components/product-card";
import {ProductType, VerificationDocumentType} from "@/libs/product";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import {
    ProductInformation, ConcernElements, OtherReplyInformation,
} from "@/views/request/received/flow/components/simple";
import {
    ViewRequest, ScipInformation,
    ProductComponentsDefinition, ManufacturingOriginDeclaration
} from "@/views/request/received/flow/components/vertiacal-steps";
import {Verification} from "@/views/product/components/product-card";
import SendReply from "@/views/request/received/flow/components/vertiacal-steps/SendReply";
import verticalStepsMixin from "@/views/request/received/flow/vertical-steps-mixin";
import DoneGoToNextStep from "@/views/request/received/flow/components/vertiacal-steps/DoneGoToNextStep";
import CustomCodes from "@/views/request/received/flow/components/vertiacal-steps/CustomCodes";
import {ProductProvider} from "@core/domain/product";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import Overlay from "@core/components/common/Overlay.vue";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";
import LegacyBulkProductInformation from "@/views/request/received/flow/components/bulk-reply/LegacyBulkProductInformation.vue";
import bulkReplyCandidatesMixin from "@/views/request/received/flow/mixins/bulk-reply-candidates-mixin";
import bulkReplyMixin from "@/views/request/received/flow/mixins/bulk-reply-mixin";

export default {
    mixins: [verticalStepsMixin, bulkReplyCandidatesMixin, bulkReplyMixin],
    components: {
        RequestLifeCycle,
        Overlay,
        InstructionText,
        BCard, BRow, BCol, Actions, ViewRequest, ProductInformation, ScipInformation, Verification,
        BOverlay, AppCollapse, AppCollapseItem, StatusBadge, ConcernElements,
        OtherReplyInformation, DeclarationApproval, SendReply, ProductComponentsDefinition, DoneGoToNextStep,
        CustomCodes, ManufacturingOriginDeclaration, LegacyBulkProductInformation,
    },
    props: {
        id: {
            required: true,
        }
    },
    data() {
        return {
            renderStage: 1,
            verificationDocuments: ['compliant_statement', 'evidence'],
            validSteps: [],
            doneClickedSteps: [],

            otherInformation: null,
            isReplyDisabled: true,
            isReplyButtonVisible: false,
            isSaveButtonVisible: false,
            productId: null,

            saveDocumentTimeout: null,
            showDocumentLoading: false,

            saveSubstanceTimeout: null,
            saveSubstanceLoading: false,

            verificationCompactView: false,
            concernElementsCompactView: false,

            requestedLegislationIds: [],

            resizeObserver: null,

            ...ProductCardMixins.data,
        };
    },
    methods: {
        markSubstanceUndefined(s) {
            this.makeSubstanceUndefined(s);
            this.saveSubstances();
        },
        markSubstanceIncluded(s) {
            this.includeConcernSubstance(s);
            this.saveSubstances();
        },
        markSubstanceExcluded(s) {
            this.excludeConcernSubstance(s);
            this.saveSubstances();
        },

        saveSubstances() {
            clearTimeout(this.saveSubstanceTimeout);
            this.saveSubstanceTimeout = setTimeout(() => {
                this.saveSubstanceLoading = true
                ProductProvider(this.getProductId())
                    .saveSubstances()
                    .finally(() => this.saveSubstanceLoading = false);
            }, 2000);
        },

        documentUpdated(document) {
            if (document.id || (!document.id && document.document)) {
                clearTimeout(this.saveDocumentTimeout);
                this.saveDocumentTimeout = setTimeout(() => {
                    this.showDocumentLoading = true;
                    ProductProvider(this.getProductId())
                        .saveVerificationDocuments()
                        .then(() => {
                            this.fetchDocuments();
                        })
                        .finally(() => this.showDocumentLoading = false);
                }, 2500);
            }
        },

        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },

        removeEvidenceReport(document, index) {
            this.evidenceReports.splice(index, 1);
        },

        close() {
            this.$router.push({
                name: 'received-requests',
            });
        },

        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },

        ...ProductCardMixins.methods,

        getProductId() {
            return this.request ? this.request.product.id : null;
        },

        changeStepValidity(step, isValid) {
            if (isValid && !this.validSteps.includes(step)) {
                this.validSteps.push(step);
            } else if (!isValid && this.validSteps.includes(step)) {
                this.validSteps.splice(this.validSteps.findIndex(s => s === step), 1);
            }
        },

        gotoNextStep(step) {
            if (step === 5) {
                if (this.isApproveDeclarationStepVisible && !this.isApproveDeclarationStepDisabled) {
                    this.$refs.step5.updateVisible();
                } else {
                    this.$refs.stepSendReply.updateVisible();
                }
            } else {
                if (5 === step) {
                    this.$refs.stepSendReply.updateVisible();
                    return;
                }
                this.$refs['step' + step].updateVisible();
            }
        },

        doneStep(step) {
            if (!this.doneClickedSteps.includes(step)) {
                this.doneClickedSteps.push(step);
            }
            const isVisible = this.isStepVisible(step + 1);
            this.markStepAsDone(step);
            if (isVisible) {
                this.gotoNextStep(step + 1);
                setTimeout(() => {
                    this.smoothScroll(step + 1);
                }, 200);
            } else {
                setTimeout(() => {
                    this.gotoNextStep(step + 1);
                    setTimeout(() => {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }, 600);
                }, 100);
            }
        },

        smoothScroll(step) {
            document.querySelector('.simple-flow-step-' + step).scrollIntoView({
                behavior: 'smooth'
            });
        },

        isStepValid(step) {
            return this.validSteps.includes(step);
        },

        isStepVisible(step) {
            switch (step) {
                case 3:
                case 'verification':
                    return this.doneSteps.includes(2) || this.doneSteps.includes(3);
                case 5:
                case 'approve-declaration':
                    return this.doneSteps.includes(4);
                case 4:
                case 'other-information':
                    return this.doneSteps.includes(3) || this.doneSteps.includes(4);
                case 2:
                case 'ce':
                    return this.doneSteps.includes(1) || this.doneSteps.includes(2);
            }
            return false;
        },

        getStepVariantStyles(step) {
            if (8 === step) {   // approve declaration
                return this.isStepValid(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
            }

            return this.isStepValid(step) && this.doneSteps.includes(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
        },
        onResize() {
            this.verificationCompactView = this.$refs.verticalStepComponent.offsetWidth < 1500;
            this.concernElementsCompactView = this.$refs.verticalStepComponent.offsetWidth < 1145;
        },

        managePredefinedDocuments() {
            //  HAM-510

            //  Reports
            if (this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                if (this.request.evidenceReportTypes.length > 0) {
                    const requestedTypes = this.request.evidenceReportTypes.reduce((requestedTypes, type) => {
                        if (!requestedTypes.hasOwnProperty(type.id)) {
                            requestedTypes[type.id] = type;
                        }
                        return requestedTypes;
                    }, {});
                    this.evidenceReports
                        .forEach((document) => {
                            document.documentTypes.map(type => {
                                if (requestedTypes.hasOwnProperty(type.id)) {
                                    delete requestedTypes[type.id];
                                }
                            });
                        })
                    ;

                    Object.keys(requestedTypes).forEach((key) => {
                        const report = this.$refs.verification.makeEvidenceReport();
                        report.documentTypes.push(requestedTypes[key]);
                        this.addReport(report);
                    });
                } else if (this.evidenceReports.length === 0) {
                    this.addReport(this.$refs.verification.makeEvidenceReport());
                }
            }

            if (
                this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)
                && this.compliantStatement.length === 0
            ) {
                this.addCompliantStatement(this.$refs.verification.makeCompliantStatement());
            }
        },
        fetched() {
            this.renderStage = 2;
            this.requestedLegislationIds = this.request.legislations.map(l => l.id);
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs.verticalStepComponent);
    },
    computed: {
        supportsLateBulkReplySelection() {
            return false;
        },
        isFinalStepVisible() {
            //  if last declarative step visible then it should be visible
            if (!this.isApproveDeclarationStepVisible) {
                return this.isStepVisible('other-information');
            }
            return this.isStepVisible('approve-declaration');
        },
        doneSteps() {
            return this.request.doneSteps ? this.request.doneSteps : [];
        },
        firstStepStyle() {
            return this.getStepVariantStyles(1);
        },
        isVerificationStepFullFiled() {
            const verificationDocuments = this.request.verificationDocuments;
            if (verificationDocuments.length === 0) {
                return true;
            }

            let states = [];
            if (verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                states.push(!(this.evidenceReports.length === 0));
                //  by default we add one empty document
                if (this.evidenceReports.length === 1 && !this.evidenceReports[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                states.push(!(this.compliantStatement.length === 0));
                //  by default we add one empty document
                if (this.compliantStatement.length === 1 && !this.compliantStatement[0].id) {
                    states.push(false);
                }
            }

            const state = states.includes(false) === false;
            this.changeStepValidity(3, state);
            return state;
        },
        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.evidenceReports.length && this.evidenceReports[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.compliantStatement.length && this.compliantStatement[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
    },
    watch: {
        evidenceReports() {
            this.managePredefinedDocuments();
        }
    }
}
</script>
<style lang="scss">
#simple-flow {
    color: black;
    .card-title, .reply-process-heading, .table, h6, label, h2, .simple-flow-instruction {
        color: black;
    }
    .verification-component .card-header {
        h4 {
            font-size: 1rem;
        }
        padding: 0.5rem 0 0.5rem 1rem;
    }
}
.vertical-steps-flow > .card {
    border: 1px solid #d5d3da !important;
}
.vertical-steps-flow > .card.done {
    border-color: #21b764b3 !important;

    background-position: right 3rem top 1.23rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2321b764b3' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: calc(0.725em + 0.438rem) calc(0.725em + 0.438rem);
}
.collapse-margin .card.border-success {
    border-bottom: 1px solid #21b764b3 !important;
}
.collapse-margin .card.border-warning {
    border-bottom: 1px solid #ffdb3c !important;
}
.vertical-steps-flow .card {
    .component-concern-elements > .card {
        border: 1px solid #d5d3da !important;
    }

    .card.component-concern-elements.has-concern-elements:not(.collapsed) > .card-title {
        border-bottom: 1px solid #d5d3da;
    }

    .card.component-concern-elements.has-concern-elements.collapsed > .card-title {
        border-bottom: none;
        margin-bottom: 0;
    }

    .card.component-concern-elements.no-concern-elements > .card-title {
        margin-bottom: 0;
    }

    .card:not(.border-success):not(.border-warning) {
        border: 1px solid rgba(34, 41, 47, 0.125) !important;
    }

    .document-row:last-child {
        margin-bottom: 2REM;
    }
}
</style>
